<template>
  <audience-group-node
    v-if="type === AudienceLogicTypes.Group"
    ref="innerRef"
    :is-editable="isEditable"
    :geofence-groups="geofenceGroups"
    :group="node as AudienceGroup"
    :parent="parent"
    :inline-popups="inlinePopups"
    @rule-changed="broadcastChange"
    @negation-changed="negationChange" />
  <audience-rule-node
    v-else-if="type === AudienceLogicTypes.Rule"
    :id="`audience-rule-node-key-${node.key}`"
    ref="innerRef"
    :is-editable="isEditable"
    :geofence-groups="geofenceGroups"
    :parent="parent"
    :is-audience-device-group="(node as AudienceRule).isAudienceDeviceIDGroup"
    :audience-rule="node as AudienceRule"
    :inline-popups="inlinePopups"
    @negation-changed="negationChange" />
</template>

<script setup lang="ts">
  import { getCurrentInstance, onBeforeUnmount, ref } from 'vue';

  import { AudienceLogicTypes } from '@/common/enums/audience-logic-types';
  import { DataProvider, GeofenceGroup } from '@/common/models';
  import AudienceGroup from '@/common/models/audience/AudienceGroup';
  import AudienceNode from '@/common/models/audience/AudienceNode';
  import AudienceRule from '@/common/models/audience/AudienceRule';
  import GlobalEventBus from '@/GlobalEventBus';
  import GlobalEvents from '@/GlobalEvents';
  import AudienceGroupNode from '@/org-app/components/audienceRules/AudienceGroupNode.vue';
  import AudienceRuleNode from '@/org-app/components/audienceRules/AudienceRuleNode.vue';
  import { mapActions } from '@/utils/StateMapping';

  // Setup
  const { updateAudienceRuleIsNegated } = mapActions();
  const props = withDefaults(
    defineProps<{
      geofenceGroups: GeofenceGroup[];
      selectedDataProvider?: DataProvider;
      isEditable: boolean;
      node: AudienceNode;
      type: AudienceLogicTypes;
      inlinePopups: boolean;
      parent?: AudienceGroup;
    }>(),
    {
      inlinePopups: false,
      parent: null,
    },
  );
  const emits = defineEmits<{
    (e: 'beforeDestroy', value: any);
  }>();

  // Reactive
  const innerRef = ref(null);

  // Expose
  defineExpose({
    broadcastChange,
    deleteSelf,
    type: props.type,
    parent: props.parent,
    innerRef,
  });

  // Methods
  function deleteSelf() {
    if (props.node.type === AudienceLogicTypes.Rule) {
      innerRef.value.deleteRule();
    } else {
      innerRef.value.deleteGroup();
    }
  }
  function negationChange(node: AudienceRule | AudienceGroup) {
    updateAudienceRuleIsNegated({ audienceRule: node, value: !node.isNegated });
    GlobalEventBus.$emit(GlobalEvents.audienceLogicExpressionChanged);
  }
  function broadcastChange() {
    GlobalEventBus.$emit(GlobalEvents.audienceLogicExpressionChanged);
  }

  // Lifecycle
  onBeforeUnmount(() => {
    const instance = getCurrentInstance();
    emits('beforeDestroy', instance.exposeProxy);
  });
</script>

<style lang="scss" rel="stylesheet/scss">
  .logical-operators {
    &.multiselect {
      width: 8.4rem;
      margin: 1.6rem 0;
    }

    &.badge {
      padding: 1rem;
      margin: 1.6rem 0;
    }
  }
</style>
